import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import VirtualCardDisplay from './components/VirtualCardDisplay';
import PaymentSourceManager from './components/PaymentSourceManager';
import CardTransactionHistory from './components/CardTransactionHistory';
import CardSecurityControls from './components/CardSecurityControls';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { notificationService } from '../../services/notificationService';
import FraudDetectionPanel from './components/FraudDetectionPanel';


const DigitalVisaCardManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [cardNotifications, setCardNotifications] = useState([]);
  const [cardStatus, setCardStatus] = useState('active');
  const [spendingLimit, setSpendingLimit] = useState(5000);
  const [availableBalance, setAvailableBalance] = useState(0);

  // Aggregate balance from all payment sources
  const paymentSources = [
    {
      id: 'btc',
      name: 'Bitcoin',
      symbol: 'BTC',
      balance: 0.8542,
      usdValue: 28450.32,
      icon: 'Bitcoin',
      color: '#F7931A',
      priority: 1,
      enabled: true
    },
    {
      id: 'eth',
      name: 'Ethereum',
      symbol: 'ETH',
      balance: 5.234,
      usdValue: 12680.50,
      icon: 'Hexagon',
      color: '#627EEA',
      priority: 2,
      enabled: true
    },
    {
      id: 'usdc',
      name: 'USD Coin',
      symbol: 'USDC',
      balance: 4520.00,
      usdValue: 4520.00,
      icon: 'DollarSign',
      color: '#10B981',
      priority: 3,
      enabled: true
    },
    {
      id: 'card',
      name: 'Linked Card',
      symbol: 'CARD',
      balance: 1600.03,
      usdValue: 1600.03,
      icon: 'CreditCard',
      color: '#8B5CF6',
      priority: 4,
      enabled: true
    }
  ];

  useEffect(() => {
    if (user?.id) {
      loadCardNotifications();
    }
  }, [user?.id]);

  const loadCardNotifications = async () => {
    try {
      const data = await notificationService?.getAll(user?.id);
      const cardRelated = data?.filter(n => 
        n?.type === 'card_purchase' || n?.type === 'suspicious_activity'
      );
      setCardNotifications(cardRelated?.slice(0, 5));
    } catch (err) {
      console.error('Failed to load card notifications:', err);
    }
  };

  useEffect(() => {
    const total = paymentSources?.filter(source => source?.enabled)?.reduce((sum, source) => sum + source?.usdValue, 0);
    setAvailableBalance(total);
  }, []);

  const cardTransactions = [
    {
      id: 1,
      merchant: 'Amazon',
      category: 'Shopping',
      amount: -89.99,
      cryptoAmount: 0.0026,
      paymentSource: 'BTC',
      date: '2026-01-17',
      time: '14:32',
      status: 'completed',
      icon: 'ShoppingBag'
    },
    {
      id: 2,
      merchant: 'Starbucks',
      category: 'Food & Dining',
      amount: -12.45,
      cryptoAmount: 12.45,
      paymentSource: 'USDC',
      date: '2026-01-17',
      time: '09:15',
      status: 'completed',
      icon: 'Coffee'
    },
    {
      id: 3,
      merchant: 'Uber',
      category: 'Transportation',
      amount: -24.50,
      cryptoAmount: 0.0098,
      paymentSource: 'ETH',
      date: '2026-01-16',
      time: '18:45',
      status: 'completed',
      icon: 'Car'
    },
    {
      id: 4,
      merchant: 'Netflix',
      category: 'Entertainment',
      amount: -15.99,
      cryptoAmount: 15.99,
      paymentSource: 'CARD',
      date: '2026-01-15',
      time: '00:01',
      status: 'completed',
      icon: 'Tv'
    },
    {
      id: 5,
      merchant: 'Whole Foods',
      category: 'Food & Dining',
      amount: -67.32,
      cryptoAmount: 0.0019,
      paymentSource: 'BTC',
      date: '2026-01-14',
      time: '16:20',
      status: 'completed',
      icon: 'ShoppingCart'
    },
    {
      id: 6,
      merchant: 'Apple Store',
      category: 'Shopping',
      amount: -1299.00,
      cryptoAmount: 0.0375,
      paymentSource: 'BTC',
      date: '2026-01-13',
      time: '11:15',
      status: 'completed',
      icon: 'Smartphone'
    },
    {
      id: 7,
      merchant: 'Shell Gas Station',
      category: 'Transportation',
      amount: -45.80,
      cryptoAmount: 0.0184,
      paymentSource: 'ETH',
      date: '2026-01-12',
      time: '08:30',
      status: 'completed',
      icon: 'Fuel'
    },
    {
      id: 8,
      merchant: 'Spotify',
      category: 'Entertainment',
      amount: -9.99,
      cryptoAmount: 9.99,
      paymentSource: 'USDC',
      date: '2026-01-11',
      time: '00:01',
      status: 'completed',
      icon: 'Music'
    }
  ];

  const handleCardFreeze = () => {
    const newStatus = cardStatus === 'active' ? 'frozen' : 'active';
    setCardStatus(newStatus);
    // Show success notification
    console.log(`Card ${newStatus === 'frozen' ? 'locked' : 'unlocked'} successfully`);
  };

  const handleTopUp = () => {
    console.log('Top up card balance');
  };

  const handleReplaceCard = () => {
    console.log('Request card replacement');
  };

  const handleReportLost = () => {
    console.log('Report card as lost/stolen');
  };

  // Add this block - declare userProfile from user context
  const userProfile = user;

  // Check if user is Pro tier
  const isProUser = userProfile?.tier === 'pro';

  if (!isProUser) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="card max-w-2xl mx-auto text-center">
            <div className="card-content py-12">
              <div className="w-20 h-20 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Crown" size={40} className="text-warning" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">Pro Feature</h2>
              <p className="text-muted-foreground mb-6">
                Digital Visa Card is available exclusively for Pro users. Upgrade your account to access virtual card payments with crypto and traditional payment sources.
              </p>
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg mb-4">
                  <Icon name="CheckCircle" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Instant crypto-to-fiat conversion</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg mb-4 ml-2">
                  <Icon name="CheckCircle" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Advanced security controls</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Real-time transaction tracking</span>
                </div>
              </div>
              <Button variant="default" size="lg" iconName="Crown">
                Upgrade to Pro - $11.99/mo
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'transactions', label: 'Transactions', icon: 'Receipt' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'sources', label: 'Payment Sources', icon: 'Wallet' }
  ];

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="CreditCard" size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Digital Visa Card</h1>
              <p className="text-muted-foreground">Manage your virtual payment card</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-warning/10 rounded-lg">
            <Icon name="Crown" size={16} className="text-warning" />
            <span className="text-sm font-semibold text-warning">Pro Feature</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Card Display and Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <VirtualCardDisplay 
                  cardStatus={cardStatus}
                  availableBalance={availableBalance}
                  spendingLimit={spendingLimit}
                />
              </div>
              <div className="space-y-4">
                {/* Quick Actions */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Quick Actions</h3>
                  </div>
                  <div className="card-content space-y-3">
                    <Button 
                      variant={cardStatus === 'active' ? 'warning' : 'success'}
                      fullWidth
                      iconName={cardStatus === 'active' ? 'Lock' : 'Unlock'}
                      onClick={handleCardFreeze}
                    >
                      {cardStatus === 'active' ? 'Lock Card' : 'Unlock Card'}
                    </Button>
                    <Button 
                      variant="outline"
                      fullWidth
                      iconName="Plus"
                      onClick={handleTopUp}
                    >
                      Top Up Balance
                    </Button>
                    <Button 
                      variant="outline"
                      fullWidth
                      iconName="RefreshCw"
                      onClick={handleReplaceCard}
                    >
                      Replace Card
                    </Button>
                    <Button 
                      variant="danger"
                      fullWidth
                      iconName="AlertTriangle"
                      onClick={handleReportLost}
                    >
                      Report Lost/Stolen
                    </Button>
                  </div>
                </div>

                {/* Card Status */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Card Status</h3>
                  </div>
                  <div className="card-content space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${
                        cardStatus === 'active' ? 'bg-success/10' : 'bg-error/10'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          cardStatus === 'active' ? 'bg-success' : 'bg-error'
                        }`} />
                        <span className={`text-sm font-semibold capitalize ${
                          cardStatus === 'active' ? 'text-success' : 'text-error'
                        }`}>
                          {cardStatus}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Card Type</span>
                      <span className="text-sm font-semibold text-foreground">Virtual Visa</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Expires</span>
                      <span className="text-sm font-semibold text-foreground">12/28</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Daily Spent</span>
                      <span className="text-sm font-semibold text-foreground">$234.76 / $1,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="card-title">Recent Activity</h3>
                    <p className="card-description">Latest card transactions</p>
                  </div>
                  <Button variant="outline" size="sm" iconName="ArrowRight" onClick={() => setActiveTab('transactions')}>
                    View All
                  </Button>
                </div>
              </div>
              <div className="card-content">
                <div className="space-y-3">
                  {cardTransactions?.slice(0, 5)?.map((transaction) => (
                    <div 
                      key={transaction?.id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name={transaction?.icon} size={20} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{transaction?.merchant}</p>
                          <p className="text-xs text-muted-foreground">{transaction?.date} at {transaction?.time}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-foreground">${Math.abs(transaction?.amount)?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{transaction?.paymentSource}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <CardTransactionHistory 
            transactions={cardTransactions}
            paymentSources={paymentSources}
          />
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CardSecurityControls cardStatus={cardStatus} />
            <FraudDetectionPanel transactions={cardTransactions} userProfile={userProfile} />
          </div>
        )}

        {/* Payment Sources Tab */}
        {activeTab === 'sources' && (
          <PaymentSourceManager sources={paymentSources} />
        )}
      </div>
    </MainLayout>
  );
};

export default DigitalVisaCardManagement;